import { postJson } from "@/domain/common/client/ApiClient"
import {
  toInputDateTime,
  toIsoFromInput,
} from "@/domain/common/utils/DateUtils"
import {
  toListItem,
  toPagingPayload,
  toSearchPayload,
} from "@/domain/tournament/client/TournamentClient"
import { EmptyResponsePayload } from "@bracket/idl/main/common/payload/EmptyRequestPayloadPt"
import { fightSportPayloadFromJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import {
  TournamentPayload,
  tournamentStatusPayloadFromJSON,
  tournamentStatusPayloadToJSON,
} from "@bracket/idl/main/tournament/model/TournamentPt"
import {
  TournamentGetByIdRequestPayload,
  TournamentGetByIdResponsePayload,
  TournamentGetByPagingRequestPayload,
  TournamentGetByPagingResponsePayload,
  TournamentRemoveRequestPayload,
  TournamentSaveRequestPayload,
  TournamentSaveResponsePayload,
} from "@bracket/idl/main/tournament/payload/TournamentPayloadPt"
import { fightSportPayloadToJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import type { TournamentSearchParam } from "@/domain/tournament/client/TournamentClient"
import type {
  TournamentFormInput,
  TournamentListItem,
} from "@/domain/tournament/model/Tournament"

interface AdminTournamentPage {
  items: TournamentListItem[]
  totalCount: number
}

/** 서버 payload → 폼 입력값 (수정 화면 프리필) */
function toFormInput(payload: TournamentPayload): TournamentFormInput {
  return {
    id: payload.id,
    name: payload.name,
    venue: payload.venue,
    startAt: toInputDateTime(payload.startAt),
    endAt: toInputDateTime(payload.endAt),
    entryOpenAt: toInputDateTime(payload.entryOpenAt),
    entryCloseAt: toInputDateTime(payload.entryCloseAt),
    status: tournamentStatusPayloadToJSON(payload.status),
    notice: payload.notice,
    sports: payload.sport.map(fightSportPayloadToJSON),
  }
}

/** 폼 입력값 → 서버 payload */
function toTournamentPayload(input: TournamentFormInput): TournamentPayload {
  return TournamentPayload.create({
    id: input.id,
    name: input.name.trim(),
    venue: input.venue.trim(),
    startAt: toIsoFromInput(input.startAt),
    endAt: toIsoFromInput(input.endAt),
    entryOpenAt: toIsoFromInput(input.entryOpenAt),
    entryCloseAt: toIsoFromInput(input.entryCloseAt),
    status: tournamentStatusPayloadFromJSON(
      input.status || "TOURNAMENT_STATUS_UNKNOWN",
    ),
    notice: input.notice,
    sport: input.sports.map(fightSportPayloadFromJSON),
  })
}

/**
 * 대회 관리 클라이언트 (관리자).
 *
 * 경로는 fight-bracket-server 의 `TournamentAdminRouter` 와 1:1 대응한다.
 * 삭제는 대회 행과 딸린 신청 · 대진을 통째로 지우는 실제 삭제라 되돌릴 수 없다.
 */
const AdminTournamentClient = {
  async getByPaging(
    param: TournamentSearchParam = {},
  ): Promise<AdminTournamentPage> {
    const request = TournamentGetByPagingRequestPayload.create({
      paging: toPagingPayload(param),
      search: toSearchPayload(param),
    })

    const json = await postJson<unknown>(
      "/bo/tournament/get-by-paging",
      TournamentGetByPagingRequestPayload.toJSON(request),
    )
    const response = TournamentGetByPagingResponsePayload.fromJSON(json)

    return {
      items: response.data.map(toListItem),
      totalCount: response.paging?.totalCount ?? 0,
    }
  },

  /** 수정 폼 프리필용 — 표시용 값이 아니라 입력 값으로 돌려준다 */
  async getFormInput(id: string): Promise<TournamentFormInput> {
    const request = TournamentGetByIdRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/tournament/get-by-id",
      TournamentGetByIdRequestPayload.toJSON(request),
    )
    const response = TournamentGetByIdResponsePayload.fromJSON(json)

    if (!response.data) throw new Error("대회를 찾을 수 없습니다.")

    return toFormInput(response.data)
  },

  /** 등록 · 수정 — id 가 비어 있으면 등록이다 */
  async save(input: TournamentFormInput): Promise<string> {
    const request = TournamentSaveRequestPayload.create({
      data: toTournamentPayload(input),
    })

    const json = await postJson<unknown>(
      "/bo/tournament/save",
      TournamentSaveRequestPayload.toJSON(request),
    )
    const response = TournamentSaveResponsePayload.fromJSON(json)

    return response.id
  },

  async remove(id: string): Promise<void> {
    const request = TournamentRemoveRequestPayload.create({ id })

    const json = await postJson<unknown>(
      "/bo/tournament/remove",
      TournamentRemoveRequestPayload.toJSON(request),
    )
    EmptyResponsePayload.fromJSON(json)
  },
}

export { AdminTournamentClient, toFormInput, toTournamentPayload }
export type { AdminTournamentPage }
